import React, { Component } from 'react'
import { connect } from "react-redux"
import * as d3 from "d3"
import omitBy from "lodash/omitBy";

import { Theme } from "@bdfx/mergado-ui-kit";

import PolygonShape from "../types/PolygonShape";
import LineShape from "../types/LineShape";

import {
    updateInputValue,
} from "app/reducers/form";

import { recalcShapeSizeAndPointsPositions, sortVerticies, LINE_CAP_CONSTS, LINE_JOIN_CONSTS, DEFAULT_LINE_CAP, DEFAULT_LINE_JOIN, getFormName } from "shape-edit/forms/helpers";

import { AlternativeTemplate, SizeType, Template } from "app/types/model-types";
import { CoordsType } from "./shape";

import { DELETE_KEY } from "app/helpers/keymap";
import { selectFormValues } from 'app/reducers/form';
import { TemplateOrAlternativeTemplateType } from 'template-edit/undoRedoReducers';
import { GradientDefs, GradientFillUrl } from "shape-edit/forms/fields";

const { abs, sin, cos, PI, max, min, round, atan2, atan, sqrt, floor } = Math

interface Props {
    isDraggable: boolean
    activeShapeId: string
    data: PolygonShape | LineShape
    size: SizeType
    isAlt: boolean
    form: TemplateOrAlternativeTemplateType
    updateInputValue: typeof updateInputValue
}

interface State {
    isActive: boolean
    activeKey: number
    dx: number
    dy: number
}

class PolygonAndPolylineSvg extends Component<Props, State> {
    handleRefs: React.RefObject<SVGRectElement | SVGCircleElement>[] = []

    constructor(props) {
        super(props)
        this.state = {
            isActive: props.activeShapeId === props.data.index,
            activeKey: null,
            dx: 0,
            dy: 0,
        }
    }

    getHandleRef = (index: number): React.RefObject<any> => {
        const refString = `phandle_${index}`
        if (typeof this.handleRefs[refString] === 'undefined') {
            this.handleRefs[refString] = React.createRef()
        }
        return this.handleRefs[refString]
    }

    setIsActive = () => {
        const { activeKey } = this.state
        const { activeShapeId, data, isDraggable } = this.props

        if (isDraggable) {
            const isActive = activeShapeId === data.index
            if (isActive) {
                this.bindAllEvents()
            }
            this.setState({ isActive: isActive, activeKey: isActive ? activeKey : null })
        }
    }

    bindEvents = (key: number) => {
        const ref = this.getHandleRef(key).current
        if (ref) {
            d3.select(ref)
                .call(
                    d3.drag()
                        .on("start", (event) => this.dragStarted(event, key))
                        .on("drag", (event) => this.dragged(event, key))
                        .on("end", (event) => this.dragEnded(event, key))
                );
        }
    }

    bindAllEvents = () => {
        const { data } = this.props
        if (data.points) {
            data.points.forEach((point, key) => {
                this.bindEvents(key)
            })
        }
    }

    componentDidMount() {
        if (this.props.isDraggable) {
            document.addEventListener("keydown", this.handleKeyDown)
            if (this.state.isActive) {
                this.bindAllEvents()
            }
        }
    }

    componentWillUnmount() {
        if (this.props.isDraggable) {
            document.removeEventListener("keydown", this.handleKeyDown)
        }
    }

    componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>, snapshot?: any) {
        if (this.props.isDraggable && prevProps && (
            this.props.activeShapeId !== prevProps.activeShapeId ||
            JSON.stringify(this.props.data.points) !== JSON.stringify(prevProps.data.points) ||
            this.props.data.angle !== prevProps.data.angle ||
            this.props.data.x !== prevProps.data.x ||
            this.props.data.y !== prevProps.data.y
        )) {
            this.setIsActive()
        }
    }

    handleKeyDown = (event) => {
        const { activeKey } = this.state
        const { data, activeShapeId, form, isAlt } = this.props
        const formName = getFormName(isAlt)

        //do not prevent default for
        if (event.target.type || !this.state.isActive || event.ctrlKey) {
            return;
        }

        switch (event.keyCode) {
            case DELETE_KEY:
                event.preventDefault()
                if (activeKey !== null) {
                    //delete one point:
                    let updatedPoints = [...data.points]
                    updatedPoints.splice(activeKey, 1)
                    this.props.updateInputValue(formName, `shapes.${data.index}.points`, updatedPoints)
                } else if (activeShapeId) {
                    //delete layer:
                    const newShapes = omitBy(form.shapes, (value, key) => key === activeShapeId)
                    this.props.updateInputValue(formName, `shapes`, newShapes)
                }
                break;
            default:
                break;
        }
    }

    dragStarted = (event, key: number) => {
        this.setState({ dx: 0, dy: 0, activeKey: key })
    }

    dragged = (event, key: number) => {
        const { dx, dy } = this.state

        //to avoid false event.dx and event.dy:
        if ((event.dx && abs(event.dx) > 100) || (event.dy && abs(event.dy) > 100)) {
            return;
        }

        this.setState({ dx: dx + event.dx || 0, dy: dy + event.dy || 0 })
    }

    dragEnded = (event, key: number) => {
        const { data, form, isAlt } = this.props
        const formName = getFormName(isAlt)

        const { dx, dy } = this.state

        const point = data.points[key]

        let updatedPoints = [...data.points]
        if (point) {
            updatedPoints[key] = {
                ...point,
                x: round(point.x + dx),
                y: round(point.y + dy),
            }
        }

        const updatedShape = recalcShapeSizeAndPointsPositions(form, { ...data, points: updatedPoints })
        this.props.updateInputValue(formName, `shapes.${data.index}`, updatedShape)
        this.setState({ dx: 0, dy: 0 })
    }

    rotateByAlpha = (coords: CoordsType): CoordsType => {
        const { data } = this.props
        const alpha = -((data.angle || 0)) / 180 * PI

        const deltaX = round((coords.x * cos(alpha) - coords.y * sin(alpha)))
        const deltaY = round((coords.x * sin(alpha) + coords.y * cos(alpha)))

        return {
            x: deltaX,
            y: deltaY,
        }
    }

    handleClickEvent = (e) => {
        const { activeShapeId, data, size, isDraggable, form, isAlt } = this.props
        const { activeKey } = this.state
        const formName = getFormName(isAlt)

        if (isDraggable) {
            const boundingClientRect = e.target.getBoundingClientRect()
            const newKey = data.points ? data.points.length : 0
            d3.select(e.target)
                .call(
                    d3.drag()
                        .on("start", (event) => this.dragStarted(event, newKey))
                        .on("drag", (event) => this.dragged(event, newKey))
                        .on("end", (event) => this.dragEnded(event, newKey))
                );

            if (activeShapeId === data.index) {
                const scaleX = boundingClientRect.width ? size.width / boundingClientRect.width : 1
                const scaleY = boundingClientRect.height ? size.height / boundingClientRect.height : 1

                const XPos = round((e.clientX - boundingClientRect.left) * scaleX - data.x)
                const YPos = round((e.clientY - boundingClientRect.top) * scaleY - data.y)

                const existingPoints: CoordsType[] = data.points ?? []
                const newPoint = this.rotateByAlpha({ x: XPos, y: YPos })

                let updatedPoints: CoordsType[] = data.type !== 'line' ? sortVerticies([...existingPoints, newPoint]) : [...existingPoints, newPoint]

                if (e.ctrlKey) {
                    const newPointIndex = updatedPoints.indexOf(newPoint)
                    const maxIndex = updatedPoints.length - 1
                    if (maxIndex && newPointIndex) {
                        const prevPoint = updatedPoints[(newPointIndex - 1) % maxIndex]
                        if (abs(prevPoint.x - newPoint.x) > abs(prevPoint.y - newPoint.y)) {
                            updatedPoints[newPointIndex] = { ...newPoint, y: prevPoint.y }
                        } else {
                            updatedPoints[newPointIndex] = { ...newPoint, x: prevPoint.x }
                        }
                    }
                }

                const updatedShape = recalcShapeSizeAndPointsPositions(form, { ...data, points: updatedPoints })

                //find the new key by coords:
                let currentPointKey = 0
                updatedPoints.forEach((point: CoordsType, key: number) => {
                    if (point.x === newPoint.x && point.y === newPoint.y) {
                        currentPointKey = key
                    }
                })
                this.setState({ activeKey: currentPointKey })

                this.props.updateInputValue(formName, `shapes.${data.index}`, updatedShape)
            }
        }
    }

    render() {
        const { data, size, isDraggable } = this.props
        const { isActive, activeKey, dx, dy } = this.state

        const circleRadius = round(max(size.width, size.height) / 80)
        const isShadow = data.shadow_color && data.shadow_sigma
        const isGradient = data.type === 'polygon' && data.gradient_id && data.gradient

        return (
            <g className="draggable" width={size.width} height={size.height}>
                {isShadow &&
                    <filter id={`shadow-${data.index}`} width="200%" height="200%" y="-50%" x="-50%">
                        <feDropShadow
                            dx={data.shadow_xoffset}
                            dy={data.shadow_yoffset}
                            stdDeviation={data.shadow_sigma}
                            floodColor={data.shadow_color}
                            floodOpacity={1 - data.shadow_opacity / 100}
                        />
                    </filter>
                }

                {isGradient &&
                    <GradientDefs gradient={data.gradient} idx={data.gradient_id} />
                }

                {isActive &&
                    <rect
                        transform={`rotate(${-(data.angle || 0)}), translate(${-data.x},${-data.y})`}
                        width={size.width}
                        height={size.height}
                        fill={'transparent'}
                        style={{
                            cursor: 'copy',
                            pointerEvents: isDraggable ? 'fill' : 'none'
                        }}
                        onClick={(e) => this.handleClickEvent(e)}
                    />
                }

                {data.points &&
                    <>
                        {data.type === 'polygon' ?
                            <polygon
                                key={`l${activeKey}`}
                                points={data.points.map((point, key) => {
                                    const xpos = point.x + (key === activeKey ? dx : 0)
                                    const ypos = point.y + (key === activeKey ? dy : 0)
                                    return `${xpos},${ypos}`
                                }).join(' ')}
                                strokeWidth={data.stroke_width}
                                stroke={data.stroke_color}
                                strokeLinejoin={data.line_join && LINE_JOIN_CONSTS[data.line_join] ? LINE_JOIN_CONSTS[data.line_join] : DEFAULT_LINE_JOIN}
                                strokeLinecap={data.line_cap && LINE_CAP_CONSTS[data.line_cap] ? LINE_CAP_CONSTS[data.line_cap] : DEFAULT_LINE_CAP}
                                fill={isGradient ? GradientFillUrl(data.gradient_id) : data.color}
                                fillOpacity={isGradient && data.gradient.opacity ? data.gradient.opacity / 100 : 1}
                                style={{ pointerEvents: 'stroke' }}
                                filter={isShadow ? `url(#shadow-${data.index})` : ''}
                            />
                            :
                            <polyline
                                points={data.points.map((point, key) => {
                                    const xpos = point.x + (key === activeKey ? dx : 0)
                                    const ypos = point.y + (key === activeKey ? dy : 0)
                                    return `${xpos},${ypos}`
                                }).join(' ')}
                                fill="none"
                                strokeWidth={data.stroke_width}
                                stroke={data.stroke_color}
                                strokeLinejoin={data.line_join && LINE_JOIN_CONSTS[data.line_join] ? LINE_JOIN_CONSTS[data.line_join] : DEFAULT_LINE_JOIN}
                                strokeLinecap={data.line_cap && LINE_CAP_CONSTS[data.line_cap] ? LINE_CAP_CONSTS[data.line_cap] : DEFAULT_LINE_CAP}
                                filter={isShadow ? `url(#shadow-${data.index})` : ''}
                            />
                        }
                    </>
                }
                {data.points &&
                    data.points.map((point, key) => {
                        const xpos = point.x + (key === activeKey ? dx : 0)
                        const ypos = point.y + (key === activeKey ? dy : 0)
                        return <g key={`pp${key}`}>
                            <circle
                                key={`p${activeKey}${key}`}
                                transform={`translate(${xpos},${ypos})`}
                                r={circleRadius}
                                fill={isActive ? (activeKey === key ? Theme.red : Theme.steelblue) : 'transparent'}
                                strokeWidth={2}
                                stroke={isActive ? Theme.grey : 'transparent'}
                                style={{ cursor: 'move' }}
                                className={'handles'}
                                ref={this.getHandleRef(key)}
                            />
                            {isActive &&
                                <text
                                    dominantBaseline={"central"}
                                    textAnchor={"middle"}
                                    fill={'#fff'}
                                    transform={`translate(${xpos},${ypos})`}
                                >{key}</text>
                            }
                        </g>
                    })
                }
            </g>
        )
    }
}

function mapStateToProps(state) {
    return {
        isAlt: state.isAlt,
        form: selectFormValues(state, getFormName(state.isAlt)),
        activeShapeId: state.activeShapeId,
    }
}

export default connect(mapStateToProps, {
    updateInputValue,
})(PolygonAndPolylineSvg)